import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import NavigationHeader from '@alltrails/shared/components/NavigationHeader';
import useIpCountryCode from 'hooks/useIpCountryCode';
import useUser from 'hooks/useUser';
import { Context } from '@alltrails/shared/types/context';
import { lazy, MouseEvent, Suspense, useState } from 'react';
import { NoSsr } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { wrapUrlSafe } from 'utils/language_support_util';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { buildSignupUrl } from 'utils/roadblocks';
import handleRoadblock from 'utils/handleRoadblock';
import MobileHomeLink from './MobileHomeLink';
import * as styles from './styles/styles.module.scss';
import CustomProvider from '../CustomProvider';

const SignupModal = lazy(() => import('components/SignupModal'));

type NavigationWrapperProps = {
  currentPage: string;
  locationSlug: string;
};

const Right = ({ currentPage, locationSlug }: NavigationWrapperProps): JSX.Element => {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();

  if (currentPage !== PageStrings.EXPLORE_ALL_PAGE) {
    if (locationSlug) {
      const href = wrapUrlSafe(`/explore/${locationSlug}?mobileMap=true`, languageRegionCode);

      return (
        <div className={styles.rightContainer}>
          <a
            className={styles.map}
            href={user ? href : buildSignupUrl(href, languageRegionCode)}
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              if (!user) {
                e.preventDefault();

                handleRoadblock({
                  eventName: 'trail_header_map',
                  languageRegionCode,
                  user,
                  returnToUrl: href,
                  afterRoadblock: href
                });
              }
            }}
          >
            <FormattedMessage defaultMessage="Map" />
          </a>
        </div>
      );
    }

    // pathname does not include the language code
    if ((__AT_DATA__.pathname === '/' || __AT_DATA__.pathname === '/?ref=header') && !user?.pro) {
      return (
        <div className={styles.rightContainer}>
          <MobileHomeLink />
        </div>
      );
    }
  }

  return <div className={styles.mobileRightSpacer} />;
};

type NavigationProps = {
  context: Context;
  currentPage: string;
  locationSlug: string;
};

const Navigation = ({ context, currentPage, locationSlug }: NavigationProps): JSX.Element => {
  const ipCountryCode = useIpCountryCode();
  const user = useUser();
  const [{ isOpen, redirectUrl }, toggleSignupModal] = useState<{ isOpen: boolean; redirectUrl?: string }>({ isOpen: false });

  return (
    <>
      <NoSsr>
        <Suspense fallback={null}>
          <SignupModal
            isOpen={isOpen}
            onRequestClose={() => {
              toggleSignupModal({ isOpen: false });
            }}
            onSuccess={() => {
              // no-op because of redirectUrl
            }}
            redirectUrl={redirectUrl}
          />
        </Suspense>
      </NoSsr>
      <NavigationHeader
        context={context}
        inUS={ipCountryCode === 'US'}
        rightChildren={<Right locationSlug={locationSlug} currentPage={currentPage} />}
        user={user}
        toggleSignupModal={(e: MouseEvent<HTMLAnchorElement>, redirectUrl: string) => {
          // !user check isn't necessary because we only call this for logged out props
          e.preventDefault();
          toggleSignupModal({ isOpen: !isOpen, redirectUrl });
        }}
      />
    </>
  );
};

function Wrapper({ context, currentPage, locationSlug }: NavigationProps) {
  return (
    <CustomProvider>
      <Navigation context={context} currentPage={currentPage} locationSlug={locationSlug} />
    </CustomProvider>
  );
}

export default Wrapper;
